/**
 * The small config sections (S6): table (row limit + density), pivot
 * (row/column/value/aggregation), map (geometry + optional color-by), note
 * (markdown only — data sections hidden by the dialog). Chart and stat live
 * in their own files.
 */
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AGGREGATION_FUNCTIONS, AGGREGATION_LABELS } from '../../../charts/types/chartTypes';
import { FieldRow, ColumnSelect, EnumSelect, type SectionProps } from './configFields';
import { nonSpatialColumnInfos } from './mapConfigColumns';

export function TableConfigSection({ values, set }: SectionProps) {
  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-table">
      <FieldRow label="Row limit">
        <Input
          type="number"
          min={1}
          max={10_000}
          className="h-7 w-28 text-xs"
          value={values.tableRowLimit}
          onChange={(e) =>
            set({ tableRowLimit: Math.max(1, Math.min(10_000, e.target.valueAsNumber || 100)) })
          }
          data-testid="add-tile-table-rowlimit"
        />
      </FieldRow>
      <FieldRow label="Density">
        <EnumSelect
          value={values.tableDensity}
          onChange={(tableDensity) => set({ tableDensity })}
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'comfortable', label: 'Comfortable' },
          ]}
          testId="add-tile-table-density"
        />
      </FieldRow>
    </div>
  );
}

export function PivotConfigSection({ values, set, columnInfos }: SectionProps) {
  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-pivot">
      <FieldRow label="Rows">
        <ColumnSelect
          value={values.pivotRow}
          onChange={(pivotRow) => set({ pivotRow })}
          columnInfos={columnInfos}
          categories={['string', 'boolean', 'date']}
          testId="add-tile-pivot-row"
        />
      </FieldRow>
      <FieldRow label="Columns">
        <ColumnSelect
          value={values.pivotColumn}
          onChange={(pivotColumn) => set({ pivotColumn })}
          columnInfos={columnInfos}
          categories={['string', 'boolean', 'date']}
          testId="add-tile-pivot-column"
        />
      </FieldRow>
      <FieldRow label="Value">
        <ColumnSelect
          value={values.pivotValue}
          onChange={(pivotValue) => set({ pivotValue })}
          columnInfos={columnInfos}
          categories={['numeric']}
          testId="add-tile-pivot-value"
        />
      </FieldRow>
      <FieldRow label="Aggregate">
        <EnumSelect
          value={values.pivotAgg}
          onChange={(pivotAgg) => set({ pivotAgg })}
          options={AGGREGATION_FUNCTIONS.filter((f) => f !== 'none').map((f) => ({
            value: f,
            label: AGGREGATION_LABELS[f],
          }))}
          testId="add-tile-pivot-agg"
        />
      </FieldRow>
    </div>
  );
}

/** '' (auto) can't be a Radix Select item value — sentinel round-trip. */
const AUTO = '__auto__';

/**
 * Map section — geometry column from the DETECTED spatial columns (Auto =
 * the map picks its first spatial column) + optional color-by over the
 * non-spatial columns. Both persist onto the tile and seed the map panel's
 * initial picker state; an in-panel pick wins afterwards (see MapTile's
 * docblock).
 */
export function MapConfigSection({
  values,
  set,
  columnInfos,
  spatialColumnNames,
}: SectionProps & { spatialColumnNames: string[] }) {
  const nonSpatial = nonSpatialColumnInfos(columnInfos, spatialColumnNames);
  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-map">
      <FieldRow label="Geometry">
        <EnumSelect
          value={values.mapGeometryColumn || AUTO}
          onChange={(v) => set({ mapGeometryColumn: v === AUTO ? '' : v })}
          options={[
            { value: AUTO, label: 'Auto (first spatial column)' },
            // `spatialColumnNames` are row KEYS (`detectSpatialColumnNames`); show
            // the display name, so a rekeyed column reads `Column 2`, not `col_1`.
            ...spatialColumnNames.map((key) => ({
              value: key,
              label: columnInfos.find((c) => c.key === key)?.name ?? key,
            })),
          ]}
          testId="add-tile-map-geometry"
        />
      </FieldRow>
      <FieldRow label="Color by">
        <ColumnSelect
          value={values.mapColorBy}
          onChange={(mapColorBy) => set({ mapColorBy })}
          columnInfos={nonSpatial}
          allowNone
          placeholder="None"
          testId="add-tile-map-colorby"
        />
      </FieldRow>
    </div>
  );
}

export function NoteConfigSection({ values, set }: SectionProps) {
  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-note">
      <Textarea
        className="min-h-32 text-sm"
        value={values.noteMarkdown}
        onChange={(e) => set({ noteMarkdown: e.target.value })}
        placeholder="Markdown — headings, lists, links…"
        data-testid="add-tile-note-textarea"
      />
      <p className="text-xs text-muted-foreground">
        Notes render as sanitized Markdown. Raw HTML stays inert.
      </p>
    </div>
  );
}
